import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { sessionManager, SessionState } from '@/lib/session/SessionManager'
import { SettingsService } from '@/lib/services/settingsService'

export interface UseSessionReturn {
  sessionState: SessionState
  timeRemainingMinutes: number
  isSessionActive: boolean
  extendSession: () => void
  forceLogout: () => void
  showWarning: boolean
}

export function useSession(): UseSessionReturn {
  const [sessionState, setSessionState] = useState<SessionState>(sessionManager.getState())
  const [showWarning, setShowWarning] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, isAuthenticated } = useAuthStore()
  
  // Check if we're on an authentication page (login, 2FA, forgot password, etc.)
  const isAuthPage = location.pathname.startsWith('/login') || 
                     location.pathname.startsWith('/two-factor') || 
                     location.pathname.startsWith('/forgot-password') ||
                     location.pathname.startsWith('/reset-password') ||
                     location.pathname === '/'

  // Handle session timeout
  const handleTimeout = useCallback(async () => {
    // Same ordering as AdminLayout logout: avoid wildcard + UnauthenticatedRedirect re-saving the current URL.
    navigate('/login?reason=timeout', { replace: true })
    await logout()
  }, [logout, navigate])

  // Handle session warning
  const handleWarning = useCallback((timeRemaining: number) => {
    setShowWarning(true)
    setSessionState(sessionManager.getState())
  }, [])

  // Extend session
  const extendSession = useCallback(() => {
    sessionManager.extendSession()
    setShowWarning(false)
    setSessionState(sessionManager.getState())
  }, [])

  // Force logout
  const forceLogout = useCallback(() => {
    sessionManager.forceLogout()
    handleTimeout().catch((error) => {
      console.error('[useSession] Failed to handle timeout logout:', error)
      navigate('/login?reason=timeout')
    })
  }, [handleTimeout, navigate])

  // Load session settings from backend and initialize session manager
  useEffect(() => {
    // Don't initialize session timeout on auth pages or if not authenticated
    if (isAuthPage || !isAuthenticated) {
      sessionManager.destroy()
      setShowWarning(false)
      return
    }

    // First, destroy any existing session to ensure clean state
    sessionManager.destroy()
    setShowWarning(false)

    // Load session settings from backend BEFORE initializing
    const loadSessionSettings = async () => {
      try {
        console.log('[useSession] Fetching security settings from backend...')
        let response
        try {
          response = await SettingsService.getSecuritySettings()
          console.log('[useSession] Full response from backend:', JSON.stringify(response, null, 2))
          console.log('[useSession] Response type check:', {
            hasSuccess: 'success' in response,
            hasData: 'data' in response,
            hasSession: response?.data && 'session' in response.data,
            responseKeys: Object.keys(response || {}),
            dataKeys: response?.data ? Object.keys(response.data) : []
          })
        } catch (apiError: any) {
          console.error('[useSession] API call failed:', apiError)
          console.error('[useSession] API error details:', {
            message: apiError?.message,
            response: apiError?.response?.data,
            status: apiError?.response?.status
          })
          throw apiError // Re-throw to be caught by outer catch
        }
        
        if (response && response.success && response.data?.session) {
          const { session_enabled, session_timeout } = response.data.session
          
          console.log('[useSession] Raw session data from backend:', {
            session_enabled,
            session_timeout,
            session_timeout_type: typeof session_timeout,
            full_session: response.data.session
          })
          
          // Ensure session_timeout is a number
          const timeoutMinutes = typeof session_timeout === 'number' 
            ? session_timeout 
            : parseInt(String(session_timeout), 10) || 30
          
          console.log('[useSession] Parsed session settings:', { 
            session_enabled, 
            session_timeout_raw: session_timeout,
            session_timeout_type: typeof session_timeout,
            timeoutMinutes,
            timeoutMinutes_type: typeof timeoutMinutes
          })
          
          // Update session manager config BEFORE initializing
          sessionManager.updateConfig({
            enabled: session_enabled ?? true,
            timeoutMinutes: timeoutMinutes
          })
          
          // Set callbacks BEFORE initializing
          sessionManager.setCallbacks({
            onWarning: handleWarning,
            onTimeout: handleTimeout
          })

          // Initialize AFTER config is updated (this will reset the session with new config)
          sessionManager.initialize()
        } else {
          console.warn('[useSession] No session data in response:', response)
          // Use defaults if no data
          sessionManager.updateConfig({
            enabled: true,
            timeoutMinutes: 30
          })
          sessionManager.setCallbacks({
            onWarning: handleWarning,
            onTimeout: handleTimeout
          })
          sessionManager.initialize()
        }
      } catch (error: any) {
        console.error('[useSession] Failed to load session settings:', error)
        console.error('[useSession] Error details:', {
          message: error?.message,
          response: error?.response?.data,
          stack: error?.stack
        })
        // Use defaults if loading fails
        sessionManager.updateConfig({
          enabled: true,
          timeoutMinutes: 30
        })
        sessionManager.setCallbacks({
          onWarning: handleWarning,
          onTimeout: handleTimeout
        })
        sessionManager.initialize()
      }
    }

    loadSessionSettings()

    // Update state periodically
    const interval = setInterval(() => {
      setSessionState(sessionManager.getState())
    }, 1000)

    return () => {
      clearInterval(interval)
      sessionManager.destroy()
    }
  }, [handleWarning, handleTimeout, isAuthPage, isAuthenticated])

  return {
    sessionState,
    timeRemainingMinutes: sessionManager.getTimeRemainingMinutes(),
    isSessionActive: sessionManager.isSessionActive(),
    extendSession,
    forceLogout,
    showWarning
  }
}

